import React, { memo } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { LOCATIONS, LEGEND } from "../../data/mapData";

/**
 * Sovereign Deployment World Map — Phase 1.
 *
 * Static command-centre map:
 *  · Deep near-black navy background
 *  · Dot-grid continents (SVG pattern clipped by country geometry)
 *  · Seafoam markers at 11 locations, sized by emphasis tier
 *  · No animation, no interaction
 *
 * Projection: geoEqualEarth (world-atlas 110m TopoJSON).
 */

const GEO_URL = "/logos/countries-110m.json";

const CATEGORY_STYLE = {
  deployment: { fill: "#A1AC9C", stroke: "#A1AC9C" },
  pursuit: { fill: "none", stroke: "#A1AC9C" },
  "delivery-experience": { fill: "#7A8896", stroke: "#7A8896" },
};

/** Marker radius per emphasis tier. */
function radius(e) {
  if (e === 3) return 6.2;
  if (e === 2) return 4.6;
  return 3.2;
}

const SovereignWorldMapImpl = () => {
  return (
    <div className="e2swm" data-testid="e2swm-root">
      <div className="e2swm-frame">
        <div className="e2swm-fig">FIG.4 &nbsp;·&nbsp; Sovereign deployment atlas</div>

        <div className="e2swm-canvas">
          <ComposableMap
            projection="geoEqualEarth"
            projectionConfig={{ scale: 165, center: [0, 15] }}
            width={980}
            height={560}
            style={{ width: "100%", height: "auto", display: "block" }}
          >
            <defs>
              {/* Dot-grid pattern that fills country geometry */}
              <pattern
                id="e2swm-dot-grid"
                x="0"
                y="0"
                width="6"
                height="6"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="1.2" cy="1.2" r="0.85" fill="#2A3A4E" />
              </pattern>
            </defs>

            {/* Solid backdrop rect */}
            <rect x="0" y="0" width="980" height="560" fill="#0A1220" />

            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={{
                      default: {
                        fill: "url(#e2swm-dot-grid)",
                        stroke: "#1A2436",
                        strokeWidth: 0.35,
                        outline: "none",
                      },
                      hover: {
                        fill: "url(#e2swm-dot-grid)",
                        stroke: "#1A2436",
                        strokeWidth: 0.35,
                        outline: "none",
                      },
                      pressed: {
                        fill: "url(#e2swm-dot-grid)",
                        stroke: "#1A2436",
                        strokeWidth: 0.35,
                        outline: "none",
                      },
                    }}
                  />
                ))
              }
            </Geographies>

            {LOCATIONS.map((loc) => {
              const s = CATEGORY_STYLE[loc.category];
              const r = radius(loc.emphasis);
              return (
                <Marker
                  key={loc.id}
                  coordinates={loc.coordinates}
                  data-testid={`e2swm-marker-${loc.id}`}
                >
                  {loc.category === "pursuit" ? (
                    <circle
                      r={r}
                      fill="none"
                      stroke={s.stroke}
                      strokeWidth={1.4}
                    />
                  ) : (
                    <>
                      <circle r={r} fill={s.fill} />
                      <circle
                        r={r + 2}
                        fill="none"
                        stroke={s.stroke}
                        strokeWidth={0.6}
                        opacity={0.35}
                      />
                    </>
                  )}
                </Marker>
              );
            })}
          </ComposableMap>

          {/* Legend, bottom-left, unobtrusive */}
          <div className="e2swm-legend" role="list">
            {LEGEND.map((item) => (
              <div className="e2swm-legend-item" role="listitem" key={item.key}>
                {item.key === "pursuit" ? (
                  <span className="e2swm-swatch e2swm-swatch-ring" aria-hidden />
                ) : item.key === "delivery-experience" ? (
                  <span className="e2swm-swatch e2swm-swatch-mute" aria-hidden />
                ) : (
                  <span className="e2swm-swatch e2swm-swatch-sea" aria-hidden />
                )}
                <span className="e2swm-legend-label">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="e2swm-caption">
          Sovereign intelligence deployed inside client environments across
          Africa, the Caribbean, and the Global South, alongside delivery
          experience in North America and the United Kingdom.
        </div>
      </div>
    </div>
  );
};

export const SovereignWorldMap = memo(SovereignWorldMapImpl);
export default SovereignWorldMap;
